import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Message as SlackIcon,
  Check as CheckIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayArrowIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import SlackWorkspaceConnector from './SlackWorkspaceConnector';
import SlackChannelSelector from './SlackChannelSelector';
import SlackBotConfiguration from './SlackBotConfiguration';

const SETUP_STEPS = [
  {
    label: 'Connect Workspace',
    description: 'Authorize FAANA to access your Slack workspace',
    icon: <SlackIcon />
  },
  {
    label: 'Select Channels',
    description: 'Choose which channels to monitor for data ingestion',
    icon: <GroupIcon />
  },
  {
    label: 'Configure Bot',
    description: 'Set up bot behavior, filters, and sync schedule',
    icon: <SettingsIcon />
  },
  {
    label: 'Review & Launch',
    description: 'Review configuration and start data ingestion',
    icon: <PlayArrowIcon />
  }
];

// eslint-disable-next-line max-lines-per-function
function SlackSetupWizard({
  entityId,
  onComplete,
  onCancel,
  initialData = {}
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [setupData, setSetupData] = useState({
    workspace: null,
    selectedChannels: [],
    botConfig: {},
    ...initialData
  });
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const handleNext = () => {
    setCompletedSteps(prev => new Set([...prev, activeStep]));
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step) => {
    // Allow navigation to completed steps or the next step
    if (completedSteps.has(step) || step <= Math.max(...completedSteps) + 1) {
      setActiveStep(step);
    }
  };

  const isStepComplete = (step) => {
    switch (step) {
      case 0: // Workspace connection
        return setupData.workspace && setupData.workspace.status === 'connected';
      case 1: // Channel selection
        return setupData.selectedChannels && setupData.selectedChannels.length > 0;
      case 2: // Bot configuration
        return setupData.botConfig && setupData.botConfig.name && setupData.botConfig.botType;
      case 3: // Review
        return false; // This step doesn't auto-complete
      default:
        return false;
    }
  };

  const canProceed = (step) => {
    return isStepComplete(step);
  };

  const handleComplete = async () => {
    if (onComplete) {
      await onComplete(setupData);
    }
  };

  const updateSetupData = (field, value) => {
    setSetupData(prev => ({ ...prev, [field]: value }));
  };

  // eslint-disable-next-line max-lines-per-function
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              First, connect your Slack workspace to FAANA. You'll need admin permissions in the workspace.
            </Alert>

            <SlackWorkspaceConnector
              entityId={entityId}
              selectedWorkspace={setupData.workspace}
              onWorkspaceSelect={(workspace) => updateSetupData('workspace', workspace)}
              onConnectionChange={() => {
                // Refresh workspace list if needed
              }}
            />

            {setupData.workspace && (
              <Card sx={{ mt: 2, bgcolor: 'success.light' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon color="success" />
                    <Typography variant="body1" fontWeight="medium">
                      Connected to {setupData.workspace.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Team ID: {setupData.workspace.teamId}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Select the channels you want to monitor. You can add or remove channels later.
            </Alert>

            <SlackChannelSelector
              entityId={entityId}
              workspaceId={setupData.workspace?.id}
              selectedChannels={setupData.selectedChannels}
              onChannelSelection={(channels) => updateSetupData('selectedChannels', channels)}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Configure how the bot should collect and process data from your selected channels.
            </Alert>

            <SlackBotConfiguration
              config={setupData.botConfig}
              onConfigChange={(config) => updateSetupData('botConfig', config)}
            />
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Configuration
            </Typography>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  <SlackIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Workspace
                </Typography>
                <Typography variant="body2">
                  {setupData.workspace?.name} (Team ID: {setupData.workspace?.teamId})
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Channels ({setupData.selectedChannels?.length || 0})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {setupData.selectedChannels?.slice(0, 10).map((channel) => (
                    <Chip key={channel.id} size="small" label={`#${channel.name}`} />
                  ))}
                  {(setupData.selectedChannels?.length || 0) > 10 && (
                    <Chip
                      size="small"
                      label={`+${setupData.selectedChannels.length - 10} more`}
                      variant="outlined"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Bot Configuration
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Bot Name"
                      secondary={setupData.botConfig?.name || 'Not set'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Bot Type"
                      secondary={setupData.botConfig?.botType === 'message_ingestion' ? 'Message Ingestion Bot' : 'Entity Creation Bot'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Sync Schedule"
                      secondary={setupData.botConfig?.realTimeSync ? 'Real-time (webhooks)' : setupData.botConfig?.syncSchedule || 'Not set'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Privacy"
                      secondary={setupData.botConfig?.privacy?.tokenizeMessages ? 'Messages will be tokenized' : 'Messages will not be tokenized'}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Ready to launch!</strong>
              </Typography>
              <Typography variant="body2">
                Your Slack bot will start collecting data from the selected channels according to your configuration.
                You can modify these settings anytime from the ingestion points management page.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        <SlackIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Slack Bot Setup Wizard
      </Typography>

      <Stepper activeStep={activeStep} orientation="vertical">
        {SETUP_STEPS.map((step, index) => (
          <Step key={step.label} completed={completedSteps.has(index)}>
            <StepLabel
              onClick={() => handleStepClick(index)}
              sx={{
                cursor: completedSteps.has(index) || index <= Math.max(...completedSteps, -1) + 1 ? 'pointer' : 'default',
                '& .MuiStepLabel-label': {
                  cursor: 'inherit'
                }
              }}
              icon={isStepComplete(index) ? <CheckIcon /> : step.icon}
            >
              <Typography variant="subtitle1" fontWeight="medium">
                {step.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step.description}
              </Typography>
            </StepLabel>
            <StepContent>
              {renderStepContent(index)}

              <Box sx={{ mb: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={index === SETUP_STEPS.length - 1 ? handleComplete : handleNext}
                  sx={{ mr: 1 }}
                  disabled={!canProceed(index)}
                >
                  {index === SETUP_STEPS.length - 1 ? 'Create Ingestion Point' : 'Continue'}
                </Button>
                <Button
                  disabled={index === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                {index === 0 && (
                  <Button onClick={onCancel}>
                    Cancel
                  </Button>
                )}
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {activeStep === SETUP_STEPS.length && (
        <Paper square elevation={0} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Setup Complete!
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Your Slack bot has been configured and will start collecting data shortly.
          </Typography>
          <Button onClick={onComplete} variant="contained">
            Return to Ingestion Points
          </Button>
        </Paper>
      )}
    </Box>
  );
}

export default SlackSetupWizard;